import { secondsToMilliseconds } from 'date-fns';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Text } from '@growtherapy/sprout-ui';

import { toast } from '../../toasts';
import { type StartVideoError, StartVideoErrorCode } from '../../video-sdk';
import { NoAccessButton } from './NoAccess';
import { CAMERA_TAKEN_TOAST_KEY, NO_ACCESS_CAMERA_TOAST_KEY } from './constants';

/**
 * Returns a callback that surfaces the toast explaining how to recover from a
 * failed camera start. New {@link StartVideoErrorCode}s fall through to the
 * browser permissions toast until they earn their own copy.
 */
export function useStartVideoErrorToast(openHelpModal?: () => void) {
  const t = useTranslate();

  return useCallback(
    (error: StartVideoError) => {
      switch (error.code) {
        case StartVideoErrorCode.CAMERA_TAKEN:
          toast.error(t('telehealth-core:controls.yourCameraIsBeingUsedByAnotherApplication'), {
            key: CAMERA_TAKEN_TOAST_KEY,
            timeout: secondsToMilliseconds(20),
          });
          return;
        default:
          toast.error(
            <Text>
              {t('telehealth-core:controls.yourBrowserDoesntHaveAccessToYourCamera')}{' '}
              <NoAccessButton
                onClick={() => {
                  toast.close(NO_ACCESS_CAMERA_TOAST_KEY);
                  openHelpModal?.();
                }}
              />
            </Text>,
            { key: NO_ACCESS_CAMERA_TOAST_KEY, timeout: secondsToMilliseconds(20) },
          );
          return;
      }
    },
    [t, openHelpModal],
  );
}
